"use client";

import React from "react";
import { Clock, ArrowLeft, ChefHat } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";


declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleRecipeConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 animate-pulse">
        <div className="h-10 w-32 bg-muted rounded-md"></div>
        <div className="h-16 w-3/4 bg-muted rounded-md"></div>
        <div className="h-[400px] w-full bg-muted rounded-xl"></div>
        <div className="flex gap-4">
          <div className="h-8 w-24 bg-muted rounded-md"></div>
          <div className="h-8 w-24 bg-muted rounded-md"></div>
        </div>
        <div className="h-64 w-full bg-muted rounded-md"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl text-center py-12">
        <p className="text-destructive mb-4">Failed to load recipe details.</p>
        <Link to="#">
          <Button variant="outline">Return to Recipes</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl text-center py-12">
        <p className="text-muted-foreground mb-4">Recipe not found.</p>
        <Link to="#">
          <Button variant="outline">Return to Recipes</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const { ingredients, cooking_time, difficulty, instructions, recipe_image } = post.customFields || {};
  
  
  const displayImage = post.featuredImage || recipe_image || "/img/generic_recipe_placeholder.png";

  const getDifficultyColor = (diff?: string) => {
    switch (diff?.toLowerCase()) {
      case 'easy':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800';
      case 'medium':
        return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
      case 'hard':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
      default:
        return 'bg-secondary text-secondary-foreground';
    }
  };

  return (
    <article className="mx-auto max-w-4xl space-y-10">
      {}
      <nav>
        <Link to="#">
          <Button variant="ghost" className="pl-0 hover:bg-transparent hover:text-primary">
            <ArrowLeft className="mr-2 size-4" />
            Back to Recipes
          </Button>
        </Link>
      </nav>

      {}
      <header className="space-y-6">
        <h1 className="font-default text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl text-foreground">
          {post.title}
        </h1>
        
        <Card className="overflow-hidden border-none shadow-lg">
          <AspectRatio ratio={16 / 9}>
            <img
              src={displayImage}
              alt={post.title}
              className="object-cover w-full h-full"
            />
          </AspectRatio>
        </Card>

        {}
        <div className="flex flex-wrap items-center gap-4 pt-2">
          {cooking_time && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-md border border-border/50 cursor-help">
                    <Clock className="size-4" />
                    <span className="text-sm font-medium">{cooking_time} mins</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Total cooking time</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {difficulty && (
            <Badge variant="outline" className={`px-3 py-1.5 text-sm ${getDifficultyColor(difficulty)}`}>
              <ChefHat className="mr-1.5 size-3.5" />
              {difficulty}
            </Badge>
          )}
        </div>
      </header>

      <Separator className="my-8" />

      {}
      <section className="space-y-4">
        <h2 className="font-default text-2xl font-semibold text-foreground">Ingredients</h2>
        <Textarea
          readOnly
          value={ingredients || "No ingredients listed."}
          className="min-h-[200px] resize-none bg-muted/30 border-border/50 text-base leading-relaxed focus-visible:ring-0 cursor-default"
          aria-label="Recipe ingredients"
        />
      </section>

      <Separator className="my-8" />

      {}
      <section className="space-y-4">
        <h2 className="font-default text-2xl font-semibold text-foreground">Cooking Instructions</h2>
        <Textarea
          readOnly
          value={instructions || "No instructions provided."}
          className="min-h-[300px] resize-none bg-muted/30 border-border/50 text-base leading-relaxed focus-visible:ring-0 cursor-default"
          aria-label="Recipe instructions"
        />
      </section>
      
      {}
      <footer className="pt-8 pb-4">
        <Link to="#">
          <Button variant="outline" size="lg" className="w-full sm:w-auto">
            <ArrowLeft className="mr-2 size-4" />
            Explore More Recipes
          </Button>
        </Link>
      </footer>
    </article>
  );
}

export default function SingleRecipeTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "recipe", per_page: 1 }; 
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-recipe-template-section" className="w-full py-12 md:py-24 lg:py-32 bg-background">
      <div className="container px-4 md:px-6 mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SingleRecipeConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}